import { useEffect, useState } from 'react';
import { Edit } from 'lucide-react';

import { AcceptType, Dropzone } from '@/components/dropzone';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { LoadingSpinner } from '@/components/loading-spinner';
import { Org } from '@/types';
import { useNotification } from '@/components/notifications';
import { OrgImage } from '@/components/org-image';
import { apiRequest } from '@/utils/api-request';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { Modal } from '@/components/modal';

interface AdminBrandingLogoProps {
  org: Org;
  onUpdate: (updates: Partial<Org>) => void;
}

export const AdminBrandingLogo = ({
  org,
  onUpdate,
}: AdminBrandingLogoProps) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showLogoText, setShowLogoText] = useState<boolean>(
    org?.branding?.show_logo_text ?? true,
  );
  const [isTogglingText, setIsTogglingText] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingToggleValue, setPendingToggleValue] = useState<boolean | null>(
    null,
  );
  const notification = useNotification();

  useEffect(() => {
    setShowLogoText(org?.branding?.show_logo_text ?? true);
  }, [org]);

  const handleUpload = async (files: File[]) => {
    setIsLoading(true);

    const formData = new FormData();
    formData.append('file', files[0]);

    try {
      const response = await apiRequest<{
        branding: { logo_url: string };
      }>({
        url: `/organizations/${org.orgid}/upload_logo`,
        method: 'PUT',
        target: 'platform',
        formData: true,
        body: formData,
      });

      if (response?.detail) {
        notification.error(response.detail);
        return;
      }

      if (response?.data?.branding?.logo_url) {
        notification.success('Logo uploaded successfully');
        setIsEditMode(false);
        onUpdate({
          branding: {
            ...org?.branding,
            logo_url: response.data.branding.logo_url,
          },
        });
      } else {
        notification.error('Failed to upload logo');
      }
    } catch (error) {
      console.error(error);
      notification.error('Failed to upload organization logo');
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleText = (checked: boolean) => {
    setPendingToggleValue(checked);
    setShowConfirmDialog(true);
  };

  const confirmToggle = async () => {
    if (pendingToggleValue === null) return;

    const checked = pendingToggleValue;
    setShowLogoText(checked);
    setIsTogglingText(true);
    setShowConfirmDialog(false);

    try {
      const updatedBranding = {
        ...org?.branding,
        show_logo_text: checked,
      };

      const response = await apiRequest({
        url: `/organizations/${org.orgid}`,
        method: 'PUT',
        target: 'platform',
        body: {
          name: org.name,
          branding: updatedBranding,
        },
      });

      if (response?.detail) {
        notification.error(response.detail);
        // Revert on error
        setShowLogoText(!checked);
        return;
      }

      notification.success(
        checked
          ? 'Logo text enabled successfully'
          : 'Logo text disabled successfully',
      );
      onUpdate({
        branding: {
          ...org?.branding,
          show_logo_text: checked,
        },
      });
    } catch (error) {
      console.error('Update logo text preference error:', error);
      notification.error('An unexpected error occurred');
      // Revert on error
      setShowLogoText(!checked);
    } finally {
      setIsTogglingText(false);
      setPendingToggleValue(null);
    }
  };

  const cancelToggle = () => {
    setShowConfirmDialog(false);
    setPendingToggleValue(null);
  };

  return (
    <Card>
      <CardContent className='flex flex-col gap-4'>
        <div className='flex justify-between w-full'>
          <div className='text-zinc-900 dark:text-zinc-50'>
            Organization logo
          </div>
          <div className='flex gap-2'>
            {isEditMode ? (
              <Button
                disabled={isLoading}
                onClick={() => setIsEditMode(false)}
                variant='ghost'
              >
                {isLoading ? <LoadingSpinner /> : 'Cancel'}
              </Button>
            ) : (
              <Button
                onClick={() => setIsEditMode(true)}
                variant='ghost'
                size='icon'
              >
                <Edit />
              </Button>
            )}
          </div>
        </div>
        {isEditMode ? (
          <Dropzone
            handleUpload={handleUpload}
            isLoading={isLoading}
            acceptType={AcceptType.IMAGE}
            multiple={false}
          />
        ) : (
          <div className='flex items-center justify-center h-10 w-fit'>
            {org?.branding?.logo_url ? (
              <OrgImage
                imageUrl={org?.branding?.logo_url}
                className='h-10 w-auto object-contain max-w-none rounded-md'
              />
            ) : (
              <span className='text-sm text-muted-foreground'>
                No logo uploaded
              </span>
            )}
          </div>
        )}

        <Separator />

        <div className='flex justify-between items-center w-full'>
          <div className='flex flex-col gap-1'>
            <div className='text-zinc-900 dark:text-zinc-50'>
              Show organization name with logo
            </div>
            <p className='text-sm text-muted-foreground'>
              Display the organization name as text alongside the logo
            </p>
          </div>
          <Switch
            checked={showLogoText}
            onCheckedChange={handleToggleText}
            disabled={isTogglingText}
          />
        </div>
      </CardContent>

      <Modal
        title='Confirm Change'
        description={
          <>
            Are you sure you want to {pendingToggleValue ? 'enable' : 'disable'}{' '}
            the organization name display? This will affect how the logo appears
            throughout the application.
          </>
        }
        isDialogOpen={showConfirmDialog}
        setIsDialogOpen={setShowConfirmDialog}
        footer={
          <>
            <Button variant='outline' onClick={cancelToggle}>
              Cancel
            </Button>
            <Button onClick={confirmToggle}>Confirm</Button>
          </>
        }
      />
    </Card>
  );
};
